import { useCustomQuery } from '@/global/hooks/useCustomQuery';
import type { AxiosError } from 'axios';
import { schoolsService, transactionsService } from '@/global/api/gen';
import type { SchoolSearchResponseDto } from '@/global/api/gen/ts/SchoolSearchResponseDto';
import type {
    SchoolsControllerGetAdminSchoolsQueryParams
} from '@/global/api/gen/ts/schoolsDTO/SchoolsControllerGetAdminSchools';
import type { SchoolSearchAdminResponseDto } from '@/global/api/gen/ts/SchoolSearchAdminResponseDto';
import type {
    SchoolsControllerGetUserSchoolsQueryParams
} from '@/global/api/gen/ts/schoolsDTO/SchoolsControllerGetUserSchools';
import { SchoolKeys } from '@/modules/schools/services/school.keys';
import type { SchoolAttributesCountDto } from '@/global/api/gen/ts/SchoolAttributesCountDto';
import type {
    SchoolsControllerGetSchoolsWithFeatureTogglesQueryParams
} from '@/global/api/gen/ts/schoolsDTO/SchoolsControllerGetSchoolsWithFeatureToggles';
import type {
    SchoolsControllerGetSchoolInfoByBinQueryParams
} from '@/global/api/gen/ts/schoolsDTO/SchoolsControllerGetSchoolInfoByBin';
import type { OmmitedUseQueryOptions } from '@/global/types';
import { useCustomMutation } from '@/global/hooks/useCustomMutation';
import type { SchoolUpdateDto } from '@/global/api/gen/ts/SchoolUpdateDto';
import type { ChangeSchoolFeatureEnableDto } from '@/global/api/gen/ts/ChangeSchoolFeatureEnableDto';
import type { AttachNewSchoolDto } from '@/global/api/gen/ts/AttachNewSchoolDto';
import type { SchoolChangeInfoUpdateDto } from '@/global/api/gen/ts/SchoolChangeInfoUpdateDto';
import type {
    SchoolsControllerGetSchoolsByUserIdQueryParams
} from '@/global/api/gen/ts/schoolsDTO/SchoolsControllerGetSchoolsByUserId';
import type { SchoolCanByCertificateBodyDto } from '@/global/api/gen/ts/SchoolCanByCertificateBodyDto';
import type { SchoolOrganizationCreateDto } from '@/global/api/gen/ts/SchoolOrganizationCreateDto';
import type { SchoolToggleCashbackDto } from '@/global/api/gen/ts/SchoolToggleCashbackDto';
import type { SchoolChangeCashbackDto } from '@/global/api/gen/ts/SchoolChangeCashbackDto';
import type { ManualTransactionCreateDto } from '@/global/api/gen/ts/ManualTransactionCreateDto';
import type {
    SchoolsControllerGetSchoolLandingQueryParams
} from '@/global/api/gen/ts/schoolsDTO/SchoolsControllerGetSchoolLanding';
import type { SchoolToggleDto } from '@/global/api/gen/ts/SchoolToggleDto';
import type { SchoolSearchLandingResponseDto } from '@/global/api/gen/ts/SchoolSearchLandingResponseDto';

export const apiGetSchoolList = (params: SchoolsControllerGetAdminSchoolsQueryParams, options?: OmmitedUseQueryOptions<SchoolSearchAdminResponseDto>) => {
    return useCustomQuery({
        queryFn: () => schoolsService.schoolsControllerGetAdminSchools(params)
            .then((res) => res.data),
        queryKey: SchoolKeys.schoolAdmin(params),
        ...options
    });
};

export const apiGetSchoolPublic = (params: SchoolsControllerGetUserSchoolsQueryParams, options?: OmmitedUseQueryOptions<SchoolSearchResponseDto>) => {
    return useCustomQuery({
        queryFn: () => schoolsService.schoolsControllerGetUserSchools(params)
            .then((res) => res.data),
        queryKey: SchoolKeys.schoolPublic(params),
        ...options
    });
};

export const apiSearchSchoolsLanding = (params: SchoolsControllerGetSchoolLandingQueryParams, options?: OmmitedUseQueryOptions<SchoolSearchLandingResponseDto>) => {
    return useCustomQuery({
        queryFn: () => schoolsService.schoolsControllerGetSchoolLanding(params)
            .then((res) => res.data),
        queryKey: SchoolKeys.schoolPublic(params),
        ...options
    });
};

export const apiGetSchoolsAttributes = (id: number) => {
    return useCustomQuery<SchoolAttributesCountDto, AxiosError>({
        enabled: !!id,
        queryFn: () => schoolsService.schoolsControllerGetSchoolAttributesCount({id})
            .then((res) => res.data),
        queryKey: SchoolKeys.attributes(id)
    });
};

export const apiGetSchoolById = (id: number) => {
    return useCustomQuery({
        enabled: !!id && !isNaN(id),
        queryFn: () => schoolsService.schoolsControllerGetSchoolById({id})
            .then((res) => res.data),
        queryKey: SchoolKeys.detail(id)
    });
};

export const apiGetSchoolListFeatureToggles = (params: SchoolsControllerGetSchoolsWithFeatureTogglesQueryParams) => {
    return useCustomQuery({
        queryFn: () => schoolsService.schoolsControllerGetSchoolsWithFeatureToggles(params)
            .then((res) => res.data),
        queryKey: SchoolKeys.featureToggles(params)
    });
};

export const apiChangeFeatureToggleSchool = () => {
    return useCustomMutation({
        mutationFn: (data: ChangeSchoolFeatureEnableDto) => schoolsService.schoolsControllerGetSchoolFeature(data)
            .then((res) => res.data),
        queryKey: () => SchoolKeys.all
    });
};

export const apiGetSchoolByBin = (bin: string, params: SchoolsControllerGetSchoolInfoByBinQueryParams, option?: OmmitedUseQueryOptions<SchoolSearchResponseDto>) => {
    return useCustomQuery({
        queryFn: () => schoolsService.schoolsControllerGetSchoolInfoByBin({bin}, params)
            .then((res) => res.data),
        queryKey: SchoolKeys.detailByBin(bin, params),
        ...option
    });
};

export const apiUpdateSchoolByAdmin = () => {
    return useCustomMutation({
        mutationFn: (data: SchoolUpdateDto) => schoolsService.schoolsControllerChangeSchoolByAdmin(data)
            .then((res) => res.data),
        queryKey: () => SchoolKeys.all
    });
};

export const apiUpdateSchool = () => {
    return useCustomMutation({
        mutationFn: (data: SchoolChangeInfoUpdateDto) => schoolsService.schoolsControllerChangeSchoolInfo(data)
            .then((res) => res.data),
        queryKey: () => SchoolKeys.all
    });
};

export const apiAttachSchoolToUser = () => {
    return useCustomMutation({
        mutationFn: (data: AttachNewSchoolDto) => schoolsService.schoolsControllerAddSchoolToUser(data)
            .then((res) => res.data),
        queryKey: () => SchoolKeys.all
    });
};

export const apiSchoolsByUser = (id: number, params: SchoolsControllerGetSchoolsByUserIdQueryParams, option?: OmmitedUseQueryOptions<SchoolSearchResponseDto>) => {
    return useCustomQuery({
        queryFn: () => schoolsService.schoolsControllerGetSchoolsByUserId({id}, params)
            .then((res) => res.data),
        queryKey: SchoolKeys.byUser(id, params),
        ...option
    });
};

export const apiChangeBuyCertificate = () => {
    return useCustomMutation({
        mutationFn: ({schoolId, ...data}: SchoolCanByCertificateBodyDto & {
            schoolId: number
        }) => schoolsService.schoolsControllerUpdateStatusCanBuyCertificate({schoolId}, data)
            .then((res) => res.data),
        queryKey: (school) => SchoolKeys.detail(school.id)
    });
};

export const apiChangeAiTest = () => {
    return useCustomMutation({
        mutationFn: ({schoolId, ...data}: & {
            schoolId: number,
            enabled: boolean
        }) => schoolsService.schoolsControllerToggleAiTest({schoolId}, data)
            .then((res) => res.data),
        queryKey: (school) => SchoolKeys.detail(school.id)
    });
};

export const apiAddOrganizationToSchool = () => {
    return useCustomMutation({
        mutationFn: ({schoolId, ...data}: SchoolOrganizationCreateDto & {
            schoolId: number
        }) => schoolsService.schoolsControllerCreateOrganization({schoolId}, data)
            .then((res) => res.data),
        queryKey: SchoolKeys.all
    });
};

export const apiDeleteOrganizationFromSchool = () => {
    return useCustomMutation({
        mutationFn: ({schoolId, organizationId}: {
            schoolId: number,
            organizationId: number
        }) => schoolsService.schoolsControllerDeleteOrganization({schoolId, organizationId})
            .then((res) => res.data),
        queryKey: SchoolKeys.all
    });
};

export const apiEnableCashback = () => {
    return useCustomMutation({
        mutationFn: ({schoolId, ...data}: SchoolToggleCashbackDto & {
            schoolId: number
        }) => schoolsService.schoolsControllerEnableCashback({schoolId}, data)
            .then((res) => res.data),
        queryKey: (school) => SchoolKeys.detail(school.id)
    });
};

export const apiChangeCashback = () => {
    return useCustomMutation({
        mutationFn: ({schoolId, ...data}: SchoolChangeCashbackDto & {
            schoolId: number
        }) => schoolsService.schoolsControllerChangeCashback({schoolId}, data)
            .then((res) => res.data),
        queryKey: (school) => SchoolKeys.detail(school.id)
    });
};

export const apiSchoolBalanceUpdate = () => {
    return useCustomMutation({
        mutationFn: (data: ManualTransactionCreateDto) => transactionsService.transactionsControllerChangeBalance(data)
            .then((res) => res.data),
        queryKey: () => SchoolKeys.all
    });
};

export const apiEnableCatalog = () => {
    return useCustomMutation({
        mutationFn: ({schoolId, ...data}: SchoolToggleDto & {
            schoolId: number
        }) => schoolsService.schoolsControllerToggleCatalog({schoolId}, data)
            .then((res) => res.data),
        queryKey: (school) => SchoolKeys.detail(school.id)
    });
};

